import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  CalendarClock,
  Camera,
  FilePlus2,
  Film,
  FolderOpen,
  Gauge,
  Image as ImageIcon,
  MapPin,
  Plus,
  ShieldAlert,
  Trash2,
  X,
} from 'lucide-react'
import { AppShell, PageHeader } from '../components/AppShell'
import { EmptyState, Pagination, Panel, SeverityPill, Spinner, StatusPill } from '../components/ui'
import { api, mediaUrl } from '../lib/api'
import { usePortal } from '../lib/portal-context'
import { cx, formatDateTime, plateDisplay, relativeTime } from '../lib/format'
import type { CaseSummary, DashboardStats, DateRange, PageInfo } from '../lib/types'

const STATUS_TABS = [
  { code: 'all', label: 'All' },
  { code: 'draft', label: 'Drafts' },
  { code: 'submitted', label: 'Submitted' },
  { code: 'under_review', label: 'Under review' },
  { code: 'notice_sent', label: 'Notice sent' },
  { code: 'resolved', label: 'Resolved' },
]

const RANGE_OPTIONS: { code: DateRange; label: string }[] = [
  { code: '7d', label: 'Last 7 days' },
  { code: '30d', label: 'Last 30 days' },
  { code: '90d', label: 'Last 90 days' },
  { code: '1y', label: 'Last 12 months' },
  { code: 'ytd', label: 'Year to date' },
  { code: 'all', label: 'All time' },
  { code: 'custom', label: 'Custom range…' },
]

// Anything at its default is left out of the URL, so a plain /?  is the
// last-30-days view and a link with query params is exactly what you were
// looking at.
const DEFAULTS: Record<string, string> = {
  status: 'all',
  violation: 'all',
  q: '',
  range: '30d',
  from: '',
  to: '',
  page: '1',
  perPage: '24',
}

const dateInput = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`

const daysAgo = (days: number) => {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return date
}

export function Dashboard() {
  const { config, can, violationLabel } = usePortal()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const [cases, setCases] = useState<CaseSummary[]>([])
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const read = (key: string) => params.get(key) ?? DEFAULTS[key]
  const status = read('status')
  const violation = read('violation')
  const query = read('q')
  const range = read('range') as DateRange
  const from = read('from')
  const to = read('to')
  const page = Math.max(1, Number(read('page')) || 1)
  const perPage = Math.max(1, Number(read('perPage')) || 24)

  const patch = useCallback(
    (next: Record<string, string | number>, resetPage = true) => {
      setParams(
        (prev) => {
          const search = new URLSearchParams(prev)
          for (const [key, value] of Object.entries(next)) {
            const text = String(value ?? '')
            if (!text || text === DEFAULTS[key]) search.delete(key)
            else search.set(key, text)
          }
          if (resetPage && !('page' in next)) search.delete('page')
          return search
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const load = useCallback(async () => {
    try {
      setError(null)
      const payload = await api.listCases({
        status,
        violation,
        q: query,
        range,
        from,
        to,
        page,
        perPage,
      })
      setCases(payload.cases)
      setPageInfo(payload.page)
      setStats(payload.stats)
      // Narrowing the filters can strand you past the last page; the server
      // clamps the request, so mirror whatever it actually served.
      if (payload.page.page !== page) patch({ page: payload.page.page }, false)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load cases.')
    } finally {
      setLoading(false)
    }
  }, [status, violation, query, range, from, to, page, perPage, patch])

  useEffect(() => {
    const timer = setTimeout(() => void load(), query ? 220 : 0)
    return () => clearTimeout(timer)
  }, [load, query])

  const filtered =
    status !== DEFAULTS.status ||
    violation !== DEFAULTS.violation ||
    query !== DEFAULTS.q ||
    range !== DEFAULTS.range

  const rangeLabel =
    range === 'custom'
      ? [from, to].filter(Boolean).join(' → ') || 'custom range'
      : (RANGE_OPTIONS.find((option) => option.code === range)?.label ?? '').toLowerCase()

  const chooseRange = (next: DateRange) => {
    // Custom starts from whatever window you were already on, so the results
    // don't jump to all-time the moment you switch.
    if (next === 'custom') {
      patch({ range: next, from: from || dateInput(daysAgo(30)), to: to || dateInput(new Date()) })
    } else {
      patch({ range: next, from: '', to: '' })
    }
  }

  const startCase = async () => {
    setCreating(true)
    try {
      const payload = await api.createCase()
      navigate(`/new?case=${payload.case.caseNumber}`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not start a new case.')
      setCreating(false)
    }
  }

  const statCards = useMemo(
    () => [
      { label: 'Total cases', value: stats?.total ?? 0, icon: FolderOpen, tone: 'text-slate-300' },
      { label: 'Open with HOA', value: stats?.open ?? 0, icon: ShieldAlert, tone: 'text-altair-400' },
      { label: 'Video clips', value: stats?.videos ?? 0, icon: Film, tone: 'text-sky-300' },
      { label: 'Last 30 days', value: stats?.last30 ?? 0, icon: CalendarClock, tone: 'text-violet-300' },
    ],
    [stats],
  )

  return (
    <AppShell
      search={query}
      onSearch={(value) => patch({ q: value })}
      actions={
        can('cases:write') ? (
          <button type="button" onClick={startCase} disabled={creating} className="btn-primary">
            {creating ? <Spinner /> : <Plus size={16} />}
            New report
          </button>
        ) : null
      }
    >
      <div className="mx-auto max-w-[1400px] px-4 py-7 sm:px-6">
        <PageHeader
          eyebrow={`${config?.community ?? 'Altair Irvine'} · Private street enforcement`}
          title="Violation cases"
          blurb={`Documented video and photo evidence of traffic and parking violations, packaged into reports you can send to ${
            config?.management ?? 'management'
          } with a link they can open and play.`}
        />

        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {statCards.map(({ label, value, icon: Icon, tone }) => (
            <div key={label} className="panel px-4 py-3.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-[0.13em] text-slate-500">
                  {label}
                </span>
                <Icon size={15} className={tone} />
              </div>
              <div className="mt-1.5 text-[26px] font-bold leading-none tracking-[-0.02em] text-white">
                {value}
              </div>
            </div>
          ))}
        </div>

        <div className="mb-5 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-1 rounded-lg border border-white/[0.07] bg-ink-900/60 p-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.code}
                type="button"
                onClick={() => patch({ status: tab.code })}
                className={cx(
                  'rounded-md px-3 py-1.5 text-[12.5px] font-medium transition',
                  status === tab.code
                    ? 'bg-white/[0.09] text-white'
                    : 'text-slate-400 hover:text-slate-200',
                )}
              >
                {tab.label}
                {tab.code === 'draft' && stats?.drafts ? (
                  <span className="ml-1.5 rounded bg-white/10 px-1 text-[10.5px]">{stats.drafts}</span>
                ) : null}
              </button>
            ))}
          </div>

          <select
            value={violation}
            onChange={(event) => patch({ violation: event.target.value })}
            className="input input-sm w-auto"
          >
            <option value="all">All violation types</option>
            {(config?.catalog.violations ?? []).map((type) => (
              <option key={type.code} value={type.code}>
                {type.label}
              </option>
            ))}
          </select>

          <select
            value={range}
            onChange={(event) => chooseRange(event.target.value as DateRange)}
            aria-label="Date range"
            className="input input-sm w-auto"
          >
            {RANGE_OPTIONS.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>

          {range === 'custom' ? (
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(event) => patch({ from: event.target.value })}
                aria-label="From date"
                className="input input-sm w-auto"
              />
              <span className="text-[12.5px] text-slate-600">to</span>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(event) => patch({ to: event.target.value })}
                aria-label="To date"
                className="input input-sm w-auto"
              />
            </div>
          ) : null}

          {filtered ? (
            <button
              type="button"
              onClick={() => patch({ status: 'all', violation: 'all', q: '', range: '30d', from: '', to: '' })}
              className="btn-subtle px-2.5 py-1.5 text-[12.5px]"
            >
              <X size={13} />
              Clear filters
            </button>
          ) : null}

          <span className="ml-auto text-[12.5px] text-slate-500">
            {loading
              ? 'Loading…'
              : `${(pageInfo?.total ?? 0).toLocaleString()} case${
                  pageInfo?.total === 1 ? '' : 's'
                }`}
          </span>
        </div>

        {error ? (
          <Panel className="mb-5" bodyClassName="text-[13px] text-red-300">
            {error}
          </Panel>
        ) : null}

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((key) => (
              <div key={key} className="panel h-[300px] animate-pulse" />
            ))}
          </div>
        ) : cases.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<Camera size={22} />}
              title={filtered ? 'No cases match those filters' : 'No cases yet'}
              action={
                filtered ? (
                  <button
                    type="button"
                    onClick={() => patch({ status: 'all', violation: 'all', q: '', range: 'all', from: '', to: '' })}
                    className="btn-primary"
                  >
                    Search all cases
                  </button>
                ) : (
                  <button type="button" onClick={startCase} className="btn-primary">
                    <FilePlus2 size={16} />
                    Submit your first report
                  </button>
                )
              }
            >
              {filtered
                ? `Nothing filed ${rangeLabel} that matches. Widen the date range or clear the other filters.`
                : 'Start a case, drop in the dashcam clip and any photos, fill in what happened, and the portal generates a linkable report for HOA management.'}
            </EmptyState>
          </Panel>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {cases.map((item) => (
                <CaseCard
                  key={item.id}
                  item={item}
                  violationLabel={violationLabel}
                  canDiscardDraft={can('cases:write')}
                  onDeleted={() => void load()}
                />
              ))}
            </div>

            {pageInfo ? (
              <Pagination
                page={pageInfo.page}
                perPage={pageInfo.perPage}
                total={pageInfo.total}
                totalPages={pageInfo.totalPages}
                onPage={(next) => {
                  patch({ page: next }, false)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                onPerPage={(next) => patch({ perPage: next })}
              />
            ) : null}
          </>
        )}
      </div>
    </AppShell>
  )
}

function CaseCard({
  item,
  violationLabel,
  canDiscardDraft = false,
  onDeleted,
}: {
  item: CaseSummary
  violationLabel: (code: string) => string
  canDiscardDraft?: boolean
  onDeleted?: () => void
}) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Drafts open in the editor when you can write; everyone else gets the report
  // view. Submitted cases use the shareable tokenized URL.
  const target =
    item.status === 'draft'
      ? canDiscardDraft
        ? `/new?case=${item.caseNumber}`
        : `/case/${item.caseNumber}`
      : `/case/${item.caseNumber}-${item.shareToken}`

  const remove = async (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDeleting(true)
    try {
      await api.deleteCase(item.caseNumber)
      onDeleted?.()
    } finally {
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  return (
    <Link
      to={target}
      className="group panel relative overflow-hidden transition hover:border-altair-500/40 hover:shadow-lift"
    >
      <div className="relative aspect-video overflow-hidden bg-ink-950">
        {item.cover ? (
          <img
            src={mediaUrl(item.cover.caseId, item.cover.file)}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover opacity-85 transition duration-500 group-hover:scale-[1.04] group-hover:opacity-100"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-slate-700">
            <Camera size={26} />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/25 to-transparent" />

        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          <StatusPill status={item.status} className="bg-black/60 backdrop-blur" />
          <SeverityPill severity={item.severity} className="bg-black/60 backdrop-blur" />
        </div>

        {item.status === 'draft' && canDiscardDraft ? (
          <div className="absolute right-2.5 top-2.5 z-10">
            {confirmDelete ? (
              <div
                className="flex items-center gap-1 rounded-lg border border-red-500/30 bg-ink-950/90 p-1 backdrop-blur"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
              >
                <button
                  type="button"
                  onClick={remove}
                  disabled={deleting}
                  className="btn-danger px-2 py-1 text-[11.5px]"
                >
                  {deleting ? <Spinner size={12} /> : <Trash2 size={12} />}
                  Delete
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    setConfirmDelete(false)
                  }}
                  className="btn-ghost px-2 py-1 text-[11.5px]"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                aria-label={`Delete draft ${item.caseNumber}`}
                title="Delete draft"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setConfirmDelete(true)
                }}
                className="rounded-md bg-black/60 p-1.5 text-slate-300 opacity-0 backdrop-blur transition hover:bg-red-500/80 hover:text-white group-hover:opacity-100 focus:opacity-100"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ) : null}

        <div className="absolute bottom-2.5 right-3 flex items-center gap-2 text-[11px] text-white/70">
          {item.counts.videos ? (
            <span className="flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 backdrop-blur">
              <Film size={11} />
              {item.counts.videos}
            </span>
          ) : null}
          {item.counts.photos ? (
            <span className="flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 backdrop-blur">
              <ImageIcon size={11} />
              {item.counts.photos}
            </span>
          ) : null}
        </div>

        <div className="absolute bottom-2.5 left-3">
          <span className="rounded bg-black/65 px-2 py-1 font-mono text-[12px] font-bold tracking-wider text-white backdrop-blur">
            {plateDisplay(item.vehicle?.plate)}
          </span>
        </div>
      </div>

      <div className="px-4 py-3.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-mono text-[12px] font-semibold tracking-wide text-altair-400">
            {item.caseNumber}
          </span>
          <span className="text-[11.5px] text-slate-500">{relativeTime(item.createdAt)}</span>
        </div>

        <h3 className="mt-1 truncate text-[15px] font-semibold leading-snug text-white">
          {item.violations.map(violationLabel).join(' · ') || 'Violation not classified'}
        </h3>

        <div className="mt-2.5 space-y-1.5 text-[12.5px] text-slate-400">
          <div className="flex items-center gap-1.5">
            <MapPin size={13} className="shrink-0 text-slate-600" />
            <span className="truncate">
              {[item.location?.street, item.location?.crossStreet].filter(Boolean).join(' at ') ||
                'Location not set'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <CalendarClock size={13} className="shrink-0 text-slate-600" />
            <span>{formatDateTime(item.occurredAt)}</span>
          </div>
          {item.estimatedSpeed ? (
            <div className="flex items-center gap-1.5">
              <Gauge size={13} className="shrink-0 text-slate-600" />
              <span>
                ~{item.estimatedSpeed} mph
                {item.postedSpeed ? (
                  <span className="text-slate-600"> in a {item.postedSpeed} zone</span>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </Link>
  )
}
